// Packages
import { GatsbyImage } from "gatsby-plugin-image"
import * as React from "react"
import { twMerge } from "tw-merge"
import useDimensions from "use-react-dimensions"

// Components
import Icon from "@components/global/icon"
import ReadMore from "@components/global/read-more"
import Slider from "@components/smartpath-new/slider"

// Utils
import { sliderProps } from "@components/smartpath-new/utils"

// Types
import { Section } from "@customTypes/pageTypes"

// Props
type Props = {
  section: Section
}

const ReadMoreIcon = ({ showAllText }) => {
  return (
    <div className={`caption flex gap-[8px] items-center text-grey-6 w-full`}>
      <div className="caption flex-shrink-0">
        {!showAllText ? "Read more" : "Read less"}
      </div>
      <hr />
      <Icon icon={!showAllText ? "openDrawer" : "closeDrawer"} />
    </div>
  )
}
// Default Component
function CommunityInvolvement(props: Props) {
  const slideShowDimensions = useDimensions({})

  return (
    <div className="bg-grey-1 container--padding flex flex-col gap-[64px] py-[64px]">
      <div
        className={`container--content_vertical w-full ${
          props.slideshow
            ? "gap-[64px] desktop:grid-cols-[480px_auto] desktop:grid-rows-[auto_auto]"
            : "gap-[32px]"
        }`}
        ref={slideShowDimensions.ref}
        style={
          {
            "--slideshowWidth": `${slideShowDimensions.dimensions.width}px`,
          } as React.CSSProperties
        }
      >
        {props.slideshow && (
          <Slider
            {...sliderProps}
            classes={{
              ...sliderProps.classes,
              active: {
                indicatorButton: `bg-grey-8`,
              },
              default: {
                ...sliderProps.classes.default,
                controlButton: `bg-transparent border-pacific-blue-4 disabled:border-grey-5`,
                controlIcon: `stroke-pacific-blue-4 group-disabled/control-button:stroke-grey-5`,
                indicatorButton: `[border:1px_solid] border-grey-8 h-[15px] rounded-full w-[15px]`,
                indicatorButtonsContainer: `mt-0`,
                slideItem: twMerge(
                  `[--padding-inline:48px] before:!content-none flex h-[327px] !p-0 w-[min(100vw-var(--padding-inline),1152px)] min-tablet:h-[480px] tablet:[--padding-inline:96px] desktop:w-[480px]`,
                ),
                titleContainer: `flex-wrap gap-x-[40px] gap-y-[32px] mb-0 min-tablet:flex-nowrap `,
                titleFullWidthContainer: `desktop:col-start-1 desktop:col-end-3`,
                track: `${sliderProps.classes.default.track}`,
                trackContainer: `overflow-hidden`,
              },
            }}
            indicatorButton={<div className=""></div>}
            slides={props.slideshow.map((slide, i) => {
              return (
                <GatsbyImage
                  alt={slide.description || ""}
                  className="max-h-[480px] max-w-[480px] mx-auto"
                  image={slide.gatsbyImageData}
                />
              )
            })}
            title={
              <div
                className="[&_*]:leading-[60px] [&_*]:text-[44px] [&_*]:!text-grey-8 [&_*]:-tracking-[.88px]"
                dangerouslySetInnerHTML={{
                  __html: props.copy.heading.childMarkdownRemark.html,
                }}
              />
            }
          />
        )}
        {props.copy.heading && !props.slideshow && (
          <div
            dangerouslySetInnerHTML={{
              __html: props.copy.heading.childMarkdownRemark.html,
            }}
          />
        )}
        {/* Copy */}
        <ReadMore
          classes={{
            default: {
              content: `leading-[32px] text-[20px] [&_*:last-child]:mb-0 ${
                props.slideshow
                  ? "desktop:col-end-3 desktop:col-start-2 desktop:row-end-3 desktop:row-start-2"
                  : "desktop:col-end-3 desktop:col-start-1"
              }`,
              lineClamp:
                "line-clamp-[10] tablet:line-clamp-[13] desktop:line-clamp-[13]",
              readMoreButton: `mt-[8px] w-full ${
                props.slideshow ? "desktop:col-end-3 desktop:col-start-2" : ""
              } `,
            },
          }}
          content={props.copy.body.childMarkdownRemark.html}
          readMoreIcon={<ReadMoreIcon />}
          useReadMoreText={false}
        />
      </div>
    </div>
  )
}

export default CommunityInvolvement
